/**
 * The selected agent's input/output contract (task 796).
 *
 * The labels are the point. The two directions are NOT enforced alike, and the
 * asymmetry is deliberate in the engine:
 *
 *   - **Input is enforced.** `executeAgentNode` validates the prepared input
 *     against `agent.input` and returns a failed node result when it does not
 *     match (`collaborationNodeExecutors.ts` — "Agent input validation failed").
 *   - **Output is advisory.** A mismatch against `agent.output` is logged as a
 *     non-fatal warning, and the node-level output check in `executeNode`
 *     explicitly exempts agent nodes from failing (LLM output is unpredictable,
 *     so the engine records the mismatch instead of failing the run).
 *
 * Both are read from the AGENT, not the node: node `input` is used to apply
 * defaults during mapping and is never validated.
 *
 * Fields are rendered with `jsonSchemaToFields`, the same converter the schema
 * builder uses, so this list and the editors elsewhere always agree on what a
 * schema contains.
 *
 * Editing (task 1310, moved from `AgentForm`): each direction's field list
 * becomes a `SchemaBuilder`, with the Enforced / Advisory tag and its reason kept
 * above the builder so the asymmetry stays visible while the contract changes.
 */
import { SchemaBuilder } from '@/components/schema-builder';
import { jsonSchemaToFields, type FieldDefinition } from '@/utils/schemaBuilder';
import type { Agent } from '@/types';

type Enforcement = 'enforced' | 'advisory';

interface SchemaColumnProps {
  title: string;
  enforcement: Enforcement;
  schema: Record<string, unknown> | null;
  /** Why this direction is enforced, or why it is not. */
  note: string;
  testId: string;
  /** Render a `SchemaBuilder` for this direction (task 1310). */
  editing?: boolean;
  onChange?: (schema: Record<string, unknown> | null) => void;
}

const badgeClasses: Record<Enforcement, string> = {
  enforced:
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  advisory: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
};

/**
 * Written out capitalised rather than left to `uppercase` styling: this word is
 * the panel's whole claim, and it should read the same when copied, announced by
 * a screen reader, or matched by a test as it does on screen.
 */
const badgeLabels: Record<Enforcement, string> = {
  enforced: 'Enforced',
  advisory: 'Advisory',
};

function FieldRow({ field }: { field: FieldDefinition }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
      <span className="font-mono text-indigo-700 dark:text-indigo-300">{field.name}</span>
      <span className="text-gray-500 dark:text-gray-400">{field.type}</span>
      {field.required && (
        <span className="text-[11px] font-medium text-red-600 dark:text-red-400">required</span>
      )}
      {field.description && (
        <span className="text-gray-500 dark:text-gray-400">{field.description}</span>
      )}
    </li>
  );
}

function SchemaColumn({ title, enforcement, schema, note, testId, editing, onChange }: SchemaColumnProps) {
  const fields = jsonSchemaToFields(schema);

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-900 dark:text-white">{title}</span>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badgeClasses[enforcement]}`}
        >
          {badgeLabels[enforcement]}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{note}</p>
      {editing ? (
        <div className="mt-2" data-testid={`${testId}-builder`}>
          <SchemaBuilder
            value={schema}
            onChange={(next) => onChange?.(next)}
            label={`${title} schema`}
          />
        </div>
      ) : fields.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1.5">
          {fields.map((field) => (
            <FieldRow key={field.id} field={field} />
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">No schema defined.</p>
      )}
    </div>
  );
}

interface AgentIoContractPanelProps {
  agent: Agent;
  /** Render a schema builder per direction (task 1310). The page passes the edited agent. */
  editing?: boolean;
  /** Called with `'input'` or `'output'` and the new schema; accumulated into changed fields. */
  onFieldChange?: (field: string, value: unknown) => void;
}

export function AgentIoContractPanel({ agent, editing = false, onFieldChange }: AgentIoContractPanelProps) {
  return (
    <section
      data-testid="agent-io-contract-panel"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Input &amp; output contract
      </h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Schemas this agent declares
      </p>

      <div className={editing ? 'mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2' : 'mt-4 flex flex-col gap-5'}>
        <SchemaColumn
          title="Input"
          enforcement="enforced"
          schema={agent.input}
          note="Validated before the agent runs — a mismatch fails this node."
          testId="agent-io-input"
          editing={editing}
          onChange={(schema) => onFieldChange?.('input', schema)}
        />
        <SchemaColumn
          title="Output"
          enforcement="advisory"
          schema={agent.output}
          note="Checked and logged after the agent runs — a mismatch never fails the node."
          testId="agent-io-output"
          editing={editing}
          onChange={(schema) => onFieldChange?.('output', schema)}
        />
      </div>
    </section>
  );
}

export default AgentIoContractPanel;
